import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/library/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/library/command";

type Option = {
  name: string;
  value: string;
};

type SearchableSelectorProps = {
  options: Option[];
  value?: string;
  placeholder?: string;
  /** Shown in the open popover search field. Defaults to `placeholder`. */
  searchInputPlaceholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  triggerClassName?: string;
  /** Merged into `PopoverContent` (e.g. min width for narrow triggers). */
  popoverContentClassName?: string;
  /**
   * Keep the caller's option order instead of sorting alphabetically.
   * Custom-field options are hand-ordered by the tenant (intake years,
   * score bands, country priority) — alphabetising those is a regression,
   * so every CF-backed picker passes this.
   */
  preserveOrder?: boolean;
  "aria-labelledby"?: string;
};

// Rank options against the search term so the best matches surface first:
//   0 — exact match (case-insensitive)
//   1 — name starts with the search term
//   2 — search term appears at a word boundary (after space / hyphen / `(`)
//   3 — search term appears anywhere in the name
// Within the same tier, fall back to alphabetical order.
// `preserveOrder` keeps the caller's own ordering: no search term means the
// list is handed back untouched, and ties inside a score tier fall back to the
// original index rather than the alphabet.
function rankOptions(
  options: Option[],
  search: string,
  preserveOrder = false
): Option[] {
  const q = search.trim().toLowerCase();
  if (!q) {
    return preserveOrder
      ? options
      : [...options].sort((a, b) => a.name.localeCompare(b.name));
  }

  const scored: Array<{ option: Option; score: number; index: number }> = [];
  options.forEach((option, index) => {
    const name = option.name.toLowerCase();
    if (!name.includes(q)) return;

    let score: number;
    if (name === q) score = 0;
    else if (name.startsWith(q)) score = 1;
    else if (new RegExp(`(?:^|[\\s\\-_/(\\[])${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(name)) score = 2;
    else score = 3;

    scored.push({ option, score, index });
  });

  scored.sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    if (preserveOrder) return a.index - b.index;
    return a.option.name.localeCompare(b.option.name);
  });

  return scored.map((s) => s.option);
}

export default function SearchableSelector({
  options,
  value,
  placeholder = "Search",
  searchInputPlaceholder,
  onChange,
  disabled,
  triggerClassName,
  popoverContentClassName,
  preserveOrder = false,
  "aria-labelledby": ariaLabelledBy,
}: SearchableSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const selected = options.find((o) => o.value === value);

  const filteredOptions = React.useMemo(
    () => rankOptions(options, search, preserveOrder),
    [options, search, preserveOrder]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          aria-labelledby={ariaLabelledBy}
          title={selected ? selected.name : placeholder}
          className={cn(
            "w-full justify-between gap-2 text-left font-normal min-h-10",
            triggerClassName
          )}
        >
          <span className="truncate min-w-0 text-left">
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        // The popover sizes to the trigger by default, which made long
        // option labels (e.g. "Close Match (70-89%)") wrap when the trigger
        // sat in a narrow grid column. `min-w` floors the panel at a
        // readable width while `max-w` keeps it from blowing past the
        // viewport on small screens.
        //
        // Bumped min-w from 14rem → 20rem so full university names like
        // "Lithuanian University of Health Sciences" actually have room
        // on one line for the common case — the trigger sits in a narrow
        // grid column, so left to its own size the popover was clipping
        // ~30% of names with nothing but an ellipsis to show for it.
        className={cn(
          "z-50 w-[var(--radix-popover-trigger-width)] p-0 overflow-hidden",
          "min-w-[20rem] max-w-[min(100vw-1.5rem,32rem)]",
          "flex flex-col overscroll-contain",
          popoverContentClassName
        )}
        // Cap total popover height so the list inside actually has a
        // bounded scroll container — Tailwind v4 has been observed to
        // drop arbitrary values containing commas in min()/max() in
        // some builds, so this is inline on purpose.
        style={{ maxHeight: "min(70vh, 22rem)" }}
      >
        {/* 🔑 Disable cmdk internal filtering */}
        <Command shouldFilter={false} className="flex min-h-0 flex-1 flex-col">
          <CommandInput
            placeholder={searchInputPlaceholder ?? placeholder}
            value={search}
            onValueChange={setSearch}
          />

          <CommandList className="max-h-none flex-1 min-h-0">
            {filteredOptions.length === 0 && (
              <CommandEmpty className="p-2 text-sm text-muted-foreground">
                No results found
              </CommandEmpty>
            )}

            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                    setSearch("");
                  }}
                  // Native `title` is the safety net for the (rare) case
                  // where even the bumped popover width isn't enough — at
                  // least the user can hover to see the full label.
                  title={option.name}
                  // Allow long names to wrap to a second line instead of
                  // being clipped by the popover's right edge.
                  // `wrap-break-word` (Tailwind v4 canonical for the old
                  // `break-words`) ensures a single really-long token
                  // with no spaces still breaks cleanly inside narrow
                  // popovers.
                  className="items-start whitespace-normal wrap-break-word leading-snug"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4 shrink-0 mt-0.5",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="flex-1 min-w-0">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
